// AppManager.tsx - Updated interfaces without ImageSizes storage

import React, { useState, useEffect } from 'react';
import { 
  doc, 
  getDoc,
  Firestore 
} from 'firebase/firestore';
import { 
  ref,
  uploadBytes,
  getDownloadURL,
  getStorage
} from 'firebase/storage';
import { Functions } from 'firebase/functions';
import { useAuth } from '../../../hooks/AuthProvider';
import OrganizationSelector from './OrganizationSelector';
import AppList from './AppList';
import WorkflowView from './dpworkflow/WorkflowView';
import ControlPanel from './ControlPanel';
import AdminPanel from './AdminPanel';
import StoreManagement from './StoreManagement';
import PermissionsManagement from './PermissionsManagement';
import NavigationSidebar from './NavigationSidebar';
import '../../../styles/AppManager.css';
import { LOCATION_OPTIONS } from '../../../config/locationConfig';

// Permission constants
const DEPLOYMENT_ADMIN_PERMISSION = 'DEPLOYMENT_ADMIN';

// Define interfaces for App data
interface Theme {
  backgroundColor: string;
  textColor: string;
  cardColor: string;
  topbarBackgroundColor: string;
  topbarIconColor: string;
  topbarBorderBottomColor: string;
  sidebarBackgroundColor: string;
  sidebarIconColor: string;
  sidebarIconColorSelected: string;
  sidebarTextColor: string;
  sidebarBorderColor: string;
  borderColor: string;
  categoryIndicatorColor: string;
  cartBackgroundColor: string;
  cartCardColor: string;
  cartTextColor: string;
  cartButtonColor: string;
  cartButtonTextColor: string;
  cartSummaryBorder: string;
  payButtonColor: string;
  payButtonTextColor: string;
  loaderColor: string;
  searchBarColor?: string;
  menuButtons?: boolean;
}

interface ThemeConfig {
  variant: 'modern-light' | 'modern-dark';
  accentColor: string;
  custom?: Theme;
}

interface DomainConfig {
  connected: boolean;
}

interface App {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  icon: string;
  url: string;
  requiredPermission: string;
  region: string;
  type: string;
  status?: string;
  deploymentApproved?: boolean;
  initializationComplete?: boolean;
  adminSetupComplete?: boolean;
  firebaseProjectId?: string;
  firebaseConfig?: FirebaseConfig;
  locationId?: string;
  organizationId: string;
  theme?: ThemeConfig;
  themeConfigured?: boolean;
  domainConfigured?: boolean;
  iconConfigured?: boolean;
  appIcon?: string;
  domains?: Record<string, DomainConfig>;
  // REMOVED: generatedImageSizes field - no longer storing binary data in Firestore
}

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

interface MetadataField {
  id: string;
  name: string;
  enabled?: boolean;
  organizationId?: string;
}

interface ThemeUpdateRequest {
  id: string;
  appId: string;
  appName: string;
  requestedBy: string;
  requestedAt: Date | any;
  themeData: Theme;
  status: 'pending' | 'approved' | 'rejected';
}

interface AppManagerProps {
  db: Firestore;
  storage: ReturnType<typeof getStorage>;
  functions: Functions;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
}

export type { Theme, ThemeConfig, App, FirebaseConfig, MetadataField, ThemeUpdateRequest, DomainConfig };

const AppManager: React.FC<AppManagerProps> = ({ 
  db, 
  storage,
  functions, 
  onError, 
  onSuccess, 
  setLoading 
}) => {
  const { currentUser, hasPermission, userPermissions } = useAuth();
  const [selectedApp, setSelectedApp] = useState<string | null>(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');
  const [appData, setAppData] = useState<App | null>(null);
  const [viewMode, setViewMode] = useState<'organizations' | 'list' | 'workflow' | 'control-panel' | 'admin-panel' | 'store-management' | 'permissions'>('organizations');
  
  const canApproveDeployment = hasPermission(DEPLOYMENT_ADMIN_PERMISSION);

  // Get organizations the user has access to
  const getUserOrganizations = (): string[] => {
    const organizations = new Set<string>();
    
    userPermissions.forEach(permission => {
      if (permission.startsWith('organizations_')) {
        const parts = permission.split('_');
        if (parts.length >= 3) {
          const orgId = parts[1];
          organizations.add(orgId);
        }
      }
    });

    return Array.from(organizations);
  };

  // Check if user has admin access to the current organization
  const hasAdminAccessToCurrentOrg = (): boolean => {
    if (!selectedOrganizationId) return false;
    return userPermissions.some(permission => 
      permission === `organizations_${selectedOrganizationId}_admin`
    );
  };

  // Handle organization selection
  const handleSelectOrganization = (organizationId: string) => {
    setSelectedOrganizationId(organizationId);
    setViewMode('list');
  };

  // Handle going back to organization selector
  const handleBackToOrganizations = () => {
    setSelectedOrganizationId('');
    setSelectedApp(null);
    setAppData(null);
    setViewMode('organizations');
  };

  // Check if user has access to any organizations on mount
  useEffect(() => {
    const organizations = getUserOrganizations();
    
    if (organizations.length === 0) {
      onError('You do not have access to any organizations. Please contact an administrator.');
      return;
    }

    // If user has access to only one organization, auto-select it
    if (organizations.length === 1) {
      handleSelectOrganization(organizations[0]);
    }
  }, [userPermissions, onError]);

// Load app data when selected
useEffect(() => {
  const loadAppData = async () => {
    if (!selectedApp) {
      setAppData(null);
      return;
    }
    
    // Check if this is a new app (temp ID that doesn't exist in Firestore yet)
    if (selectedApp.includes('temp-')) {
      // This is a new app - create mock app data for the workflow
      const newAppLocationId = sessionStorage.getItem('newAppLocationId');
      const newAppOrgId = sessionStorage.getItem('newAppOrgId');
      
      if (!newAppLocationId || !newAppOrgId || newAppOrgId !== selectedOrganizationId) {
        onError('Invalid new app data. Please try again.');
        setSelectedApp(null);
        return;
      }

      // Find the location details
      const location = LOCATION_OPTIONS.find((loc: any) => loc.id === newAppLocationId);
      if (!location) {
        onError('Invalid location for new app.');
        setSelectedApp(null);
        return;
      }

      // Create mock app data for new app workflow
      const mockAppData: App = {
        id: selectedApp,
        name: '', // Will be filled in workflow
        description: '', // Will be filled in workflow
        enabled: true,
        icon: "app-default",
        url: "",
        requiredPermission: "USER",
        region: location.appRegion,
        type: "single-store",
        status: "new", // Special status for new apps
        deploymentApproved: false,
        initializationComplete: false,
        adminSetupComplete: false,
        locationId: newAppLocationId,
        organizationId: selectedOrganizationId,
        theme: {
          variant: 'modern-light',
          accentColor: '#4169e1'
        },
        themeConfigured: false,
        domainConfigured: false,
        iconConfigured: false
      };

      setAppData(mockAppData);
      setViewMode('workflow'); // Always go to workflow for new apps
      return;
    }
    
    // Existing app - load from Firestore
    setLoading(true);
    try {
      const appRef = doc(db, 'apps', selectedApp);
      const appSnapshot = await getDoc(appRef);
      
      if (appSnapshot.exists()) {
        const data = appSnapshot.data() as App;
        
        // Verify the app belongs to the selected organization
        if (data.organizationId !== selectedOrganizationId) {
          onError('Access denied: This app does not belong to the selected organization.');
          setSelectedApp(null);
          return;
        }
        
        setAppData(data);
        
        // Check if app is fully completed to show control panel
        if (data.adminSetupComplete) {
          setViewMode('control-panel');
        } else {
          setViewMode('workflow');
        }
        
      } else {
        setAppData(null);
        onError(`App with ID ${selectedApp} not found.`);
      }
      
    } catch (err) {
      console.error('Error loading app data:', err);
      onError('Failed to load app data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  loadAppData();
}, [db, selectedApp, selectedOrganizationId, setLoading, onError]);

  // Handle back to list view
  const handleBackToList = () => {
    setViewMode('list');
    setSelectedApp(null);
  };

  // Handle switching to workflow view from control panel
  const handleViewWorkflow = () => {
    setViewMode('workflow');
  };

  // Handle switching to admin panel
  const handleViewAdminPanel = () => {
    setViewMode('admin-panel');
  };

  // Handle switching to store management
  const handleViewStores = () => {
    setViewMode('store-management');
  };

  // Handle switching to permissions management
  const handleViewPermissions = () => {
    setViewMode('permissions');
  };

  // Refresh app data
  const refreshAppData = async () => {
    if (!selectedApp) return;
    
    try {
      const appRef = doc(db, 'apps', selectedApp);
      const appSnapshot = await getDoc(appRef);
      
      if (appSnapshot.exists()) {
        const data = appSnapshot.data() as App;
        setAppData(data);
      }
    } catch (err) {
      console.error('Error refreshing app data:', err);
    }
  };

  // Get the active navigation item based on current view
  const getActiveNavigationItem = (): 'app-management' | 'admin-panel' | 'permissions' => {
    switch (viewMode) {
      case 'admin-panel':
        return 'admin-panel';
      case 'permissions':
        return 'permissions';
      default:
        return 'app-management';
    }
  };

  // Show sidebar for all views except organizations selector
  const showSidebar = viewMode !== 'organizations' && viewMode !== 'workflow' && viewMode !== 'control-panel';

  return (
    <div className="app-manager">
      {viewMode === 'organizations' ? (
        <OrganizationSelector 
          onSelectOrganization={handleSelectOrganization}
        />
      ) : (
        <div className="applist-container">
          {/* Left Sidebar Navigation - Now centralized */}
          {showSidebar && (
            <NavigationSidebar
              selectedOrganizationId={selectedOrganizationId}
              activeItem={getActiveNavigationItem()}
              onBackToOrganizations={handleBackToOrganizations}
              onNavigateToApps={handleBackToList}
              onNavigateToAdmin={canApproveDeployment ? handleViewAdminPanel : () => {}}
              onNavigateToPermissions={hasAdminAccessToCurrentOrg() ? handleViewPermissions : undefined}
            />
          )}

          {/* Main Content Area */}
          <div className="applist-main-content" style={{overflowY: "scroll"}}>
            {viewMode === 'list' && (
              <AppList
                db={db}
                functions={functions}
                onError={onError}
                onSuccess={onSuccess}
                setLoading={setLoading}
                onSelectApp={setSelectedApp}
                canApproveDeployment={canApproveDeployment}
                onViewAdminPanel={canApproveDeployment ? handleViewAdminPanel : undefined}
                onViewPermissions={hasAdminAccessToCurrentOrg() ? handleViewPermissions : undefined}
                selectedOrganizationId={selectedOrganizationId}
                onBackToOrganizations={handleBackToOrganizations}
              />
            )}

            {viewMode === 'workflow' && (
              <WorkflowView
                db={db}
                storage={storage}
                functions={functions}
                appData={appData}
                onError={onError}
                onSuccess={onSuccess}
                setLoading={setLoading}
                onBack={handleBackToList}
                onAppDataUpdate={refreshAppData}
                selectedOrganizationId={selectedOrganizationId} // ← ADD THIS LINE
              />
            )}

            {viewMode === 'control-panel' && (
              <ControlPanel
                db={db}
                functions={functions}
                appData={appData}
                onError={onError}
                onSuccess={onSuccess}
                setLoading={setLoading}
                onBack={handleBackToList}
                onViewWorkflow={handleViewWorkflow}
                onViewStores={handleViewStores}
                canApproveDeployment={canApproveDeployment}
                selectedOrganizationId={selectedOrganizationId}
                onBackToOrganizations={handleBackToOrganizations}
                onViewAdminPanel={canApproveDeployment ? handleViewAdminPanel : undefined}
                onViewPermissions={hasAdminAccessToCurrentOrg() ? handleViewPermissions : undefined}
              />
            )}

            {viewMode === 'store-management' && (
              <StoreManagement
                db={db}
                functions={functions}
                appData={appData!}
                onError={onError}
                onSuccess={onSuccess}
                setLoading={setLoading}
                onBack={() => setViewMode('control-panel')}
                selectedOrganizationId={selectedOrganizationId}
                onBackToOrganizations={handleBackToOrganizations}
                onViewAdminPanel={canApproveDeployment ? handleViewAdminPanel : undefined}
                onViewPermissions={hasAdminAccessToCurrentOrg() ? handleViewPermissions : undefined}
              />
            )}

            {viewMode === 'permissions' && (
              <PermissionsManagement
                selectedOrganizationId={selectedOrganizationId}
                onBackToOrganizations={handleBackToOrganizations}
                onNavigateToApps={handleBackToList}
                onNavigateToAdmin={canApproveDeployment ? handleViewAdminPanel : undefined}
                onNavigateToPermissions={handleViewPermissions}
                db={db}
                functions={functions}
                onError={onError}
                onSuccess={onSuccess}
                setLoading={setLoading}
              />
            )}

            {viewMode === 'admin-panel' && (
              <AdminPanel
                db={db}
                functions={functions}
                onError={onError}
                onSuccess={onSuccess}
                setLoading={setLoading}
                onBack={handleBackToList}
                selectedOrganizationId={selectedOrganizationId}
                onBackToOrganizations={handleBackToOrganizations}
                onViewPermissions={hasAdminAccessToCurrentOrg() ? handleViewPermissions : undefined}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppManager;